import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ZoneData } from '../api/types';
import { tradingZonesQueryOptions } from '../queries/tradingZones';

type FilterFieldNames = 'state' | 'resource' | 'system' | 'zone';

type BaseZoneRecord = {
  value: string;
  label: string;
};

type ResourceTypeRecord = {
  predicate: {
    resource_type: 'Regulated surface water' | 'Unregulated surface water' | 'Groundwater';
    water_type: 'Surface water' | 'Groundwater';
  };
} & BaseZoneRecord;

type WaterSystemRecord = {
  predicate: {
    resource_type: 'Regulated surface water' | 'Unregulated surface water' | 'Groundwater';
    water_type: 'Surface water' | 'Groundwater';
    state: string;
    zoneId: string;
  };
} & BaseZoneRecord;

export type ZoneRecord = {
  predicate: {
    resource_type: 'Regulated surface water' | 'Unregulated surface water' | 'Groundwater';
    water_type: 'Surface water' | 'Groundwater';
    state: string;
    zoneId: string;
  };
} & BaseZoneRecord;

export type ZoneContextData = {
  states: Array<BaseZoneRecord>;
  resourceTypes: Array<ResourceTypeRecord>;
  waterSystems: Array<WaterSystemRecord>;
  zones: Array<ZoneRecord>;
};

// match stateMap keys exactly defined in ZoneData
const mapStates = (): Array<BaseZoneRecord> => {
  const stateMap = {
    'New South Wales (NSW)': 'New South Wales',
    'Victoria (VIC)': 'Victoria',
    'Queensland (QLD)': 'Queensland',
    'South Australia (SA)': 'South Australia',
    'Australian Capital Territory (ACT)': 'Australian Capital Territory'
  } as const;

  return (Object.keys(stateMap) as Array<keyof typeof stateMap>).map((key) => ({
    value: key,
    label: stateMap[key]
  }));
};

const mapResourceTypes = (): Array<ResourceTypeRecord> => [
  {
    value: 'regulated',
    label: 'Regulated surface water',
    predicate: { resource_type: 'Regulated surface water', water_type: 'Surface water' }
  },
  {
    value: 'unregulated',
    label: 'Unregulated surface water',
    predicate: { resource_type: 'Unregulated surface water', water_type: 'Surface water' }
  },
  {
    value: 'groundwater',
    label: 'Groundwater',
    predicate: { resource_type: 'Groundwater', water_type: 'Groundwater' }
  }
];

const processWaterSystems = (zoneData: Array<ZoneData>): Array<WaterSystemRecord> => {
  const records: Record<string, WaterSystemRecord> = {};

  for (const zone of zoneData) {
    const match = zone.water_system.match(/\(([^)]+)\)/);
    const zoneId = match ? match[1] : null;
    if (!zoneId) continue;

    const rowId = `${zoneId}:${zone.resource_type}:${zone.water_type}`;
    if (!records[rowId]) {
      records[rowId] = {
        value: zone.water_system,
        label: zone.water_system,
        predicate: {
          state: zone.state,
          water_type: zone.water_type,
          resource_type: zone.resource_type,
          zoneId
        }
      };
    }
  }

  return Object.values(records);
};

const processZone = (zone: ZoneData): ZoneRecord | undefined => {
  const match = zone.water_system.match(/\(([^)]+)\)/);
  const zoneId = match ? match[1] : null;
  if (!zoneId) return;

  return {
    value: zone.water_markets_trading_zone,
    label: zone.water_markets_trading_zone,
    predicate: {
      state: zone.state,
      water_type: zone.water_type,
      resource_type: zone.resource_type,
      zoneId
    }
  };
};

const processZones = (zoneData: Array<ZoneData>): Array<ZoneRecord> => {
  const records: Record<string, ZoneRecord> = {};

  for (const zone of zoneData) {
    const record = processZone(zone);
    if (!record) continue;
    if (!records[record.value]) {
      records[record.value] = record;
    }
  }

  return Object.values(records);
};

const processZoneData = (zoneData: Array<ZoneData>): ZoneContextData => ({
  states: mapStates(),
  resourceTypes: mapResourceTypes(),
  waterSystems: processWaterSystems(zoneData),
  zones: processZones(zoneData)
});

export type ZoneDataContextType = {
  data: ZoneContextData | null;
  rawZoneData: Array<ZoneData> | undefined;
  loading: boolean;
  error: string | Error | null;
  selectedZone: ZoneRecord | null;
  setSelectedZone: (zone: string) => boolean;
  resetSelectedZone: () => void;
  saveFilters: (filters: Record<FilterFieldNames, string | null> | null) => void;
  getSavedFilters: () => Record<FilterFieldNames, string | null>;
};

// Create default nulled context, provider creates
const ZoneDataContext = createContext<ZoneDataContextType>({
  data: null,
  rawZoneData: undefined,
  loading: true,
  error: null,
  selectedZone: null,
  setSelectedZone: () => false,
  resetSelectedZone: () => {},
  saveFilters: () => {},
  getSavedFilters: () => ({}) as any
});

export const useZoneDataContext = () => useContext(ZoneDataContext);

type Props = {
  children: React.ReactNode;
};

// TODO: fetch the selection from local storage and set on the provider
const STORAGE_KEY_SELECTION = 'zoneData:selection';
const STORAGE_KEY_FILTERS = 'zoneData:filters';

export const ZoneDataProvider: React.FC<Props> = ({ children }) => {
  const { data: zoneData, isLoading: loading, error: queryError } = useQuery(tradingZonesQueryOptions);
  const error = queryError ?? null;

  // Derive processed data directly from server state — no local copy needed
  const data = useMemo(() => (zoneData && zoneData.length > 0 ? processZoneData(zoneData) : null), [zoneData]);
  const initialSelectionSet = useRef(false);
  const [selectedZone, setSelectedZone] = useState<ZoneRecord | null>(null);

  const initSelectedZoneFromStorage = useCallback(
    (fetchedData: Array<ZoneData> | null) => {
      const urlZoneMatch = window.location.pathname.match(/\/decisions\/([^/?#]+)/);
      const urlZone = urlZoneMatch ? decodeURIComponent(urlZoneMatch[1]) : null;

      const lsZone = localStorage.getItem(STORAGE_KEY_SELECTION);
      const zoneToLoad = urlZone ?? (typeof lsZone === 'string' ? lsZone.trim() : null);

      if (!zoneToLoad) return;
      try {
        const matchingZone = (fetchedData || zoneData)?.find((zd) => zd.water_markets_trading_zone === zoneToLoad);
        if (matchingZone) {
          setSelectedZone(processZone(matchingZone) ?? null);
        }
      } catch {
        console.warn('Could not fetch zone selection from local storage');
      }
    },
    [zoneData]
  );

  // Restore the selected zone from URL/localStorage once — not on every refetch
  useEffect(() => {
    if (!initialSelectionSet.current && zoneData && zoneData.length > 0) {
      initialSelectionSet.current = true;
      initSelectedZoneFromStorage(zoneData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneData]); // initSelectedZoneFromStorage intentionally excluded - only run once when zoneData loads

  const handleZoneSelection = useCallback(
    (zone: string) => {
      const matchingZone = zoneData?.find((zd) => zd.water_markets_trading_zone === zone);
      if (matchingZone) {
        const zoneRecord = processZone(matchingZone) ?? null;
        if (zoneRecord) {
          setSelectedZone(zoneRecord);
          localStorage.setItem(STORAGE_KEY_SELECTION, zone);
          return true;
        }
      }

      return false;
    },
    [zoneData]
  );

  const handleResetSelectedZone = useCallback(() => {
    setSelectedZone(null);
    localStorage.removeItem(STORAGE_KEY_SELECTION);
    localStorage.removeItem(STORAGE_KEY_FILTERS);
  }, []);

  const handleSaveFilters = useCallback((filters: Record<FilterFieldNames, string | null> | null) => {
    if (!filters) return;
    localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(filters));
  }, []);

  const handleGetSavedFilters = useCallback((): Record<FilterFieldNames, string | null> => {
    try {
      const json = JSON.parse(localStorage.getItem(STORAGE_KEY_FILTERS) || '{}');
      if (
        typeof json === 'object' &&
        json !== null &&
        !Array.isArray(json) &&
        Object.prototype.toString.call(json) === '[object Object]' &&
        Object.keys(json).length === 4 &&
        Object.values(json).every(
          (value) =>
            value === null || (typeof value === 'string' && value.trim() !== '' && /[a-z0-9\s()-]+/i.test(value))
        )
      ) {
        return json;
      }
    } catch (e) {
      console.warn('Could not decode saved filters');
    }

    return {
      state: null,
      resource: null,
      system: null,
      zone: null
    };
  }, []);

  return (
    <ZoneDataContext.Provider
      value={{
        data,
        rawZoneData: zoneData,
        loading,
        error,
        selectedZone,
        setSelectedZone: handleZoneSelection,
        resetSelectedZone: handleResetSelectedZone,
        getSavedFilters: handleGetSavedFilters,
        saveFilters: handleSaveFilters
      }}
    >
      {children}
    </ZoneDataContext.Provider>
  );
};
